import { useEffect, useState } from "react";
import { confirmMfa, setupMfa } from "../api/mfa";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";
import { getStoredUser, homeForRole, storeUser } from "../utils/session";

function getUser() {
  return getStoredUser();
}

function redirectForRole(role) {
  const params = new URLSearchParams(window.location.search);
  window.location.href = params.get('production_setup') === '1' ? (params.get('return') || '/register?source=demo') : homeForRole(role);
}

export default function MfaSetup() {
  const [code, setCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const sendCode = async () => {
    setSending(true);
    setError("");
    setMessage("");

    try {
      const res = await setupMfa();
      const data = res.data?.data || {};

      setMaskedEmail(data.email || "");
      setMessage("Verification code sent. Check your email.");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to send verification code."));
    } finally {
      setLoading(false);
      setSending(false);
    }
  };

  useEffect(() => {
    sendCode();
  }, []);

  const finishSetup = () => {
    const user = getUser();
    const updatedUser = {
      ...user,
      mfa_required: true,
      mfa_enabled: true,
    };

    storeUser(updatedUser);
    redirectForRole(updatedUser.role);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await confirmMfa(code);
      finishSetup();
    } catch (err) {
      setError(getErrorMessage(err, "Invalid verification code."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card mfa-card">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="brand-mark">CV</div>

          <div>
            <h4 className="mb-0">CounselVault</h4>
            <div className="text-muted small">Email verification required</div>
          </div>
        </div>

        <h2 className="fw-bold mb-2">Verify Your Email</h2>

        <p className="text-muted">
          Enter the 6-digit verification code sent to your email address.
          This helps protect staff and attorney accounts.
        </p>

        {maskedEmail && (
          <div className="alert alert-light border py-2">
            Code sent to <strong>{maskedEmail}</strong>
          </div>
        )}

        <StatusMessage type="success">{message}</StatusMessage>
        <StatusMessage type="warning">{error}</StatusMessage>

        {loading ? (
          <div className="text-muted">Sending verification code...</div>
        ) : (
          <form onSubmit={handleConfirm}>
            <div className="mb-4">
              <label className="form-label">Verification Code</label>

              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="form-control form-control-lg"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                maxLength="6"
                placeholder="123456"
                required
                autoFocus
              />
            </div>

            <AsyncButton className="btn btn-dark btn-lg w-100" busy={saving} busyText="Verifying…">
              Verify and Continue
            </AsyncButton>

            <button
              type="button"
              className="btn btn-link w-100 mt-3"
              disabled={sending}
              onClick={sendCode}
            >
              {sending ? "Sending..." : "Send a new code"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
